/**
 * Fiat-M / USDT-M 与二级 Tab（Crypto 等）的过滤与排序。
 * 与 MarketList.filterGroupMarketRecord 中非 spot、非 optional 分支保持一致，供列表与 Favourite 推荐共用。
 */

import { getDynamicFiatMarketCodes } from "@/utils/fiatMarket";
import { isFiatQuoteCurrency } from "@/utils/getFiatCurrencyMap";

const FIAT_QUOTES_DEFAULT = ["AED", "BRL", "SAR"];

/** U 本位稳定币计价（与 trade tabCfg usds.buyCoinAry 一致，含 USDC） */
export const USDT_MARGIN_QUOTE_CODES = ["usdt", "pax", "tusd", "ausd", "usdc", "ust", "xtusd", "busd"];

/**
 * CFD symbol 的计价币（如 btc_sar_cfd → SAR）。与 Fiat-M 行内 quote 语义一致。
 */
export function getCfdQuoteCurrencyUpper(symbol) {
  const parts = String(symbol || "").split("_");
  if (parts.length >= 3 && String(parts[parts.length - 1]).toLowerCase() === "cfd") {
    return (parts[parts.length - 2] || "").toUpperCase();
  }
  if (parts.length > 1) return (parts[1] || "").toUpperCase();
  return "";
}

/**
 * 当前页为法币本位 / U 本位时，CFD 交易对是否匹配（用于列表、Top Search）
 * @param activeFiatQuoteLower 法币本位下当前交易对计价币（如 brl），仅展示该法币计价的交易对
 */
export function matchCfdSymbolMarginQuote(symbol, isFiatContractPage, isCfdPage = true, activeFiatQuoteLower = null) {
  if (!isCfdPage) return true;
  if (!String(symbol || "").toLowerCase().endsWith("_cfd")) return false;
  const quoteUpper = getCfdQuoteCurrencyUpper(symbol);
  const quoteLower = quoteUpper.toLowerCase();
  const isFiat = isFiatQuoteCurrency(quoteUpper);
  if (isFiatContractPage) {
    if (!isFiat) return false;
    if (activeFiatQuoteLower) {
      return quoteLower === String(activeFiatQuoteLower).toLowerCase();
    }
    return true;
  }
  return !isFiat;
}

export function isUsdtMarginQuoteCode(quote) {
  return USDT_MARGIN_QUOTE_CODES.includes(String(quote || "").toLowerCase());
}

/**
 * Favourite 空态推荐用法币命中列表（不含 USDT；推荐侧再并上 USDT）。
 * 与 recommendedCurrencyList plate 258、Fiat-M activeFiatCodes 对齐。
 */
export function resolveAllowedFiatQuotesForRecommend(activeFiatCodes, activeFiatCode) {
  if (Array.isArray(activeFiatCodes) && activeFiatCodes.length > 0) {
    const a = activeFiatCodes.map((c) => (c || "").toUpperCase()).filter(Boolean);
    if (a.length) return a;
  }
  if (activeFiatCode) {
    return [String(activeFiatCode).toUpperCase()];
  }
  const dynamic = getDynamicFiatMarketCodes();
  if (dynamic.length > 0) {
    return dynamic.map((c) => String(c).toUpperCase());
  }
  return [...FIAT_QUOTES_DEFAULT];
}

/**
 * 某条 ticker 是否应出现在指定 CFD Tab + subTab 列表中（不含 optional / spot）
 */
export function includeInCfdMarketTabRow(item, marketConfig, uiMarketType, subTab, activeFiatCodes, activeFiatCode) {
  const symbol = item.s;
  if (!symbol || !String(symbol).toLowerCase().endsWith("_cfd")) return false;

  const theMarket = marketConfig[symbol];
  if (!theMarket || theMarket.state === "DELISTED") return false;

  const cfgType = theMarket.type;
  if (cfgType === undefined || cfgType === null) return false;

  const quoteCurrency = getCfdQuoteCurrencyUpper(symbol);
  const quoteLower = quoteCurrency.toLowerCase();
  const normalizedType = String(cfgType).toUpperCase();

  if (uiMarketType === "fiat-m") {
    if (normalizedType !== "CFD") return false;
    // 父组件传入的数组（含空数组）表示「当前业务允许的法币计价」；仅在未传 activeFiatCodes 时用默认三币，避免 Favourite 推荐与 Fiat-M 白名单不一致
    let allowed;
    if (Array.isArray(activeFiatCodes)) {
      allowed = activeFiatCodes.map((c) => (c || "").toUpperCase()).filter(Boolean);
    } else if (activeFiatCode) {
      allowed = [String(activeFiatCode || "").toUpperCase()];
    } else {
      allowed = [...FIAT_QUOTES_DEFAULT];
    }
    if (!allowed.length) return false;
    if (!allowed.includes(quoteCurrency)) return false;
  } else if (uiMarketType === "usdt-m") {
    if (normalizedType !== "CFD") return false;
    if (isFiatQuoteCurrency(quoteCurrency)) return false;
    // 与交易页 Market tabCfg usds / commodities / stocks：仅 U 本位稳定币计价（buyCoinAry）
    if (!isUsdtMarginQuoteCode(quoteLower)) return false;
  } else {
    return false;
  }

  if (subTab && (uiMarketType === "fiat-m" || uiMarketType === "usdt-m")) {
    const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
    const has260 = plates.includes(260);
    const has261 = plates.includes(261);
    if (subTab === "commodities" && !has260) return false;
    if (subTab === "stocks" && !has261) return false;
    if (subTab === "crypto" && (has260 || has261)) return false;
  }

  return true;
}

export function mergeTickerWithWs(item, wsData) {
  const wsItem = (wsData || []).find((d) => d.s === item.s);
  return wsItem || item;
}

/** 与 MarketList doSortArray 在无 sortBy、非 optional 时一致：displayWeight 降序 */
export function sortListByDisplayWeightDesc(items, marketConfig) {
  if (!marketConfig || !Array.isArray(items)) return items;
  items.sort((x, y) => {
    const s1 = marketConfig[x.s] ? parseInt(marketConfig[x.s].displayWeight, 10) || 0 : 0;
    const s2 = marketConfig[y.s] ? parseInt(marketConfig[y.s].displayWeight, 10) || 0 : 0;
    return s2 - s1;
  });
  return items;
}

/**
 * 构建与行情页 Fiat-M/Crypto 或 USDT-M/Crypto 相同过滤与排序后的列表（已合并 ws）
 */
export function buildOrderedCfdTabList(records, { marketConfig, wsData, uiMarketType, subTab, activeFiatCodes, activeFiatCode }) {
  const out = [];
  for (const item of records || []) {
    if (!includeInCfdMarketTabRow(item, marketConfig, uiMarketType, subTab, activeFiatCodes, activeFiatCode)) continue;
    out.push(mergeTickerWithWs(item, wsData));
  }
  sortListByDisplayWeightDesc(out, marketConfig);
  return out;
}
